import { useEffect, useMemo } from 'react';

import { isEqual, keys } from 'lodash-es';
import { useCustomCompareMemo } from 'use-custom-compare';

import { WasmUnavailableError, generateShareId } from '@change-corgi/core/longlinks';

import { getNormalizedLongUrl } from './api/getNormalizedLongUrl';
import { useShortUrlsState } from './context';
import type { ShortUrlEntry, ShortUrlsCacheState } from './context/types';

function isConfigsEqual(c1: Record<string, string>, c2: Record<string, string>): boolean {
	if (!isEqual(keys(c1), keys(c2))) {
		return false;
	}
	// oxlint-disable-next-line typescript/no-unsafe-type-assertion
	return keys(c1).every((key) => c1[key as never] === c2[key as never]);
}

// Internal hook shared by `useShortUrls` and `useShortUrlsInfo`. Owns the
// `urlsMapArg` deep-compare memoization and the side-effect that enqueues
// missing URLs for the shortener. `generateShareId` is async (xxhash-wasm),
// so the local hash is computed inside the effect rather than during render.
export function useShortUrlsLoader<T extends Record<string, string>>(
	urlsMapArg: T,
): { urlsMap: T; state: ShortUrlsCacheState } {
	const [state, { addToQueue }] = useShortUrlsState();

	// to avoid re-rendering when the object is new but the contents are the same
	const urlsMap = useCustomCompareMemo(
		() => urlsMapArg,
		[urlsMapArg],
		([c1], [c2]) => isConfigsEqual(c1, c2),
	);

	const longUrls = useMemo(() => Object.values(urlsMap), [urlsMap]);

	const sourcesToLoad = useMemo(
		() =>
			longUrls
				.filter((url) => !!url && !state.cache[url])
				.map((longUrl) => ({ longUrl, normalizedLongUrl: getNormalizedLongUrl(longUrl) })),
		[longUrls, state],
	);

	useEffect(() => {
		if (!sourcesToLoad.length) return undefined;

		let cancelled = false;
		void (async () => {
			let entries: ShortUrlEntry[];
			try {
				entries = await Promise.all(
					sourcesToLoad.map(async ({ longUrl, normalizedLongUrl }) => ({
						longUrl,
						normalizedLongUrl,
						localShareId: await generateShareId(normalizedLongUrl),
					})),
				);
			} catch (err) {
				if (err instanceof WasmUnavailableError) {
					// Expected: iOS Lockdown Mode / in-app webview without WebAssembly.
					// Short URL loading is skipped; callers fall back to local share IDs.
					return;
				}
				// Unexpected WASM or network failure — re-throw so it surfaces as an
				// unhandled rejection and reaches error monitoring.
				throw err;
			}

			// oxlint-disable-next-line typescript/no-unnecessary-condition
			if (cancelled) return;

			addToQueue(entries);
		})();

		return () => {
			cancelled = true;
		};
	}, [addToQueue, sourcesToLoad]);

	return { urlsMap, state };
}
